import usePromotionVisibilityStore from '@store/promotionVisibilityStore';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import {
  dispatchProgrammaticScrollEvent,
  isPromotionSectionScrollRequestActive,
  startPromotionSectionScrollRequest,
  usePromotionSectionPosition,
} from './usePromotionSectionPosition';

interface UseAnchorScrollParams {
  order: number;
  enabled?: boolean;
  delay?: number;
  waitForLoading?: boolean;
  isLoading?: boolean;
  maxWaitTime?: number;
  isDesktop?: boolean;
}

/**
 * URL의 anchorId 파라미터를 확인하고 해당 모듈로 스크롤하는 훅
 * @param order - 모듈의 순서 번호
 * @param delay - 스크롤 지연 시간 (기본값: 100ms)
 * @param waitForLoading - 상품 로딩 완료를 기다릴지 여부
 * @param isLoading - 상품 로딩 상태
 * @param maxWaitTime - 최대 대기 시간 (기본값: 5000ms)
 */
export const useAnchorScroll = ({
  order,
  enabled = true,
  delay = 100,
  waitForLoading = false,
  isLoading = false,
  maxWaitTime = 5000,
  isDesktop,
}: UseAnchorScrollParams) => {
  const queryClient = useQueryClient();
  const { scrollToElement, stopScrollAdjustment } = usePromotionSectionPosition(
    { isDesktop, maxWaitTime }
  );

  // 스크롤 실행 여부를 추적하는 ref
  const hasScrolledRef = useRef(false);
  const isScrollingRef = useRef(false);
  const checkLoadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  // URL에서 anchorId를 제거한 뒤에도 effect를 재시작하지 않고 최신 로딩 상태를 읽는다.
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  // 해당 섹션의 상품 로딩 완료를 기다리는 함수
  const waitForProductsToLoad = useCallback(
    async (sectionNumber: number): Promise<void> => {
      const { accumulatedQueryStatus } = usePromotionVisibilityStore.getState();
      if (!accumulatedQueryStatus) return Promise.resolve();

      const sectionId = sectionNumber.toString();
      const queryStatus = accumulatedQueryStatus.get(sectionId);

      if (!queryStatus || queryStatus.isSuccess) {
        const isRecommendLoading = queryClient.isFetching({
          queryKey: ['promotionProductsRecommend'],
        });

        if (isRecommendLoading > 0) {
          return new Promise<void>(resolve => {
            const checkInterval = window.setInterval(() => {
              const currentRecommendLoading = queryClient.isFetching({
                queryKey: ['promotionProductsRecommend'],
              });
              if (currentRecommendLoading === 0) {
                window.clearInterval(checkInterval);
                resolve();
              }
            }, 100);

            setTimeout(() => {
              window.clearInterval(checkInterval);
              resolve();
            }, 5000);
          });
        }
        return Promise.resolve();
      }

      if (queryStatus.isLoading || queryStatus.isFetching) {
        return new Promise<void>(resolve => {
          const checkInterval = setInterval(() => {
            const currentStatus = usePromotionVisibilityStore
              .getState()
              .accumulatedQueryStatus.get(sectionId);
            if (
              currentStatus &&
              (currentStatus.isSuccess || currentStatus.isError)
            ) {
              window.clearInterval(checkInterval);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            window.clearInterval(checkInterval);
            resolve();
          }, 5000);
        });
      }

      return Promise.resolve();
    },
    [queryClient]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const urlParams = new URLSearchParams(window.location.search);
    const anchorId = urlParams.get('anchorId');
    const anchorIdNumber = parseInt(anchorId || '0', 10);

    // anchorId가 없거나 order와 다르면 초기화
    if (!anchorId || parseInt(anchorId, 10) !== order) {
      hasScrolledRef.current = false;
      isScrollingRef.current = false;
      return undefined;
    }

    // 이미 스크롤이 실행되었으면 무시
    if (hasScrolledRef.current || isScrollingRef.current) {
      return undefined;
    }

    if (anchorId && parseInt(anchorId, 10) === order) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('anchorId');
      window.history.replaceState({}, '', currentUrl.toString());

      isScrollingRef.current = true;
      const scrollRequestId = startPromotionSectionScrollRequest();
      const initialElement = document.getElementById(`section${order}`);
      if (initialElement) {
        dispatchProgrammaticScrollEvent(
          window,
          initialElement.getBoundingClientRect().top + window.pageYOffset
        );
      }

      const performScroll = async () => {
        const element = document.getElementById(`section${order}`);
        if (element) {
          await waitForProductsToLoad(anchorIdNumber);
          if (isPromotionSectionScrollRequestActive(scrollRequestId)) {
            await scrollToElement(element, () =>
              isPromotionSectionScrollRequestActive(scrollRequestId)
            );
          }
        }

        hasScrolledRef.current = true;
        isScrollingRef.current = false;
      };

      if (waitForLoading) {
        const startTime = Date.now();

        const checkLoading = () => {
          if (hasScrolledRef.current || !isScrollingRef.current) {
            if (checkLoadingTimerRef.current) {
              clearTimeout(checkLoadingTimerRef.current);
              checkLoadingTimerRef.current = null;
            }
            return;
          }

          const elapsedTime = Date.now() - startTime;

          if (!isLoadingRef.current || elapsedTime >= maxWaitTime) {
            checkLoadingTimerRef.current = setTimeout(performScroll, delay);
          } else {
            checkLoadingTimerRef.current = setTimeout(checkLoading, 100);
          }
        };

        checkLoading();
      } else {
        checkLoadingTimerRef.current = setTimeout(performScroll, delay);
      }

      // cleanup 함수
      return () => {
        if (checkLoadingTimerRef.current) {
          clearTimeout(checkLoadingTimerRef.current);
          checkLoadingTimerRef.current = null;
        }
        stopScrollAdjustment();
      };
    }

    return undefined;
  }, [
    order,
    enabled,
    delay,
    waitForLoading,
    maxWaitTime,
    waitForProductsToLoad,
    scrollToElement,
    stopScrollAdjustment,
  ]);
};
